import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  query,
  orderBy,
  where,
  runTransaction
} from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { 
  PlusCircle, Send, Volume2, VolumeX, Eye, Bot, MoreVertical, Edit2, Trash2, 
  CheckCircle, MessageSquarePlus, Search, MoreHorizontal, Image as ImageIcon, 
  Paperclip, Smile, ChevronUp, ChevronDown, BarChart2, Menu, X
} from 'lucide-react';
import { auth, db } from './firebaseConfig';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import html2canvas from 'html2canvas';
import LinkedInHeader from './LinkedInHeader';
import MobileBottomNav from './MobileBottomNav';
import Logo from './components/Logo';
import './LinkedInTheme.css';

// --- Components ---

const RetryIndicator = ({ retryCount }) => {
  const { t } = useTranslation();
  return (
    <div className="li-retry-indicator" style={{ padding: '8px', backgroundColor: '#fff3cd', color: '#856404', textAlign: 'center', borderRadius: '4px', marginBottom: '8px' }}>
      {t('retryIndicator', { retryCount })}
    </div>
  );
};

function SessionOptionsMenu({ onRename, onDelete, isVisible }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isVisible && !isOpen) return null; // Only render if visible or menu is open

  return (
    <div className="li-session-options" ref={menuRef} style={{ position: 'relative' }}>
      <button onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }} className="artdeco-button artdeco-button--circle artdeco-button--muted" style={{ visibility: isVisible ? 'visible' : 'hidden' }}>
        <MoreVertical size={16} />
      </button>
      {isOpen && (
        <div className="li-dropdown-menu" style={{ 
          position: 'absolute', 
          right: 0, 
          top: '100%', 
          backgroundColor: 'white', 
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', 
          borderRadius: '8px', 
          zIndex: 1000, 
          minWidth: '120px',
          padding: '4px 0'
        }}>
          <button onClick={(e) => { e.stopPropagation(); onRename(); setIsOpen(false); }} className="li-dropdown-item" style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '14px', color: '#333' }}>
            <Edit2 size={14} /> {t('rename')}
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); setIsOpen(false); }} className="li-dropdown-item" style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '14px', color: '#cc1016' }}>
            <Trash2 size={14} /> {t('delete')}
          </button>
        </div>
      )}
    </div>
  );
}

function Chat({ user, initialSessionId }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [prompt, setPrompt] = useState('');
  const [displayedConversation, setDisplayedConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [topic] = useState('General');
  const [sessionId, setSessionId] = useState(null);
  const [sessionName, setSessionName] = useState('');
  const [error, setError] = useState(null);
  const [sessions, setSessions] = useState({
    today: [],
    yesterday: [],
    previous7Days: [],
    previous30Days: [],
    older: []
  });
  const [audioUrl, setAudioUrl] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingSessionName, setEditingSessionName] = useState('');
  const audioRef = useRef(null);
  const chatContainerRef = useRef(null);
  const ansBadgeRef = useRef(null);
  const [showAudioPrompt, setShowAudioPrompt] = useState(false);
  const [isInitializingSession, setIsInitializingSession] = useState(false);
  const [talentCard, setTalentCard] = useState(null);
  const [talentCardUrl, setTalentCardUrl] = useState(null);
  const [showAnsModal, setShowAnsModal] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileSessionList, setShowMobileSessionList] = useState(false);
  
  // Agent Performance Stats
  const [totalDigitalTwins, setTotalDigitalTwins] = useState(0);
  const [totalConversations, setTotalConversations] = useState(0);
  const [screeningsPassed, setScreeningsPassed] = useState(0);
  const [notification, setNotification] = useState({ show: false, message: '' });
  
  // Profile Data
  const [localUserProfile, setLocalUserProfile] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');

  // Mobile Menu State (Lifted from MobileBottomNav)
  const [showBusinessMenu, setShowBusinessMenu] = useState(false);
  const [showMeMenu, setShowMeMenu] = useState(false);

  // --- Effects & Handlers ---

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('reset') === 'true') {
      setSessionId(null);
      setSessionName('');
      setDisplayedConversation([]);
      setTalentCard(null);
      setTalentCardUrl(null);
      localStorage.removeItem('sessionId');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.search]);

  useEffect(() => {
    if (user) {
      const userDocRef = doc(db, 'users', user.uid);
      const unsubscribe = onSnapshot(userDocRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          const profile = data.profile || {};
          // Prioritize profile.roles to match App.js logic, fallback to root roles
          setLocalUserProfile({ ...profile, roles: profile.roles || data.roles || [] });
          setFirstName(profile.firstName || '');
          setLastName(profile.lastName || '');
          setAvatarUrl(profile.avatar_url || '');
        }
      });
      return () => unsubscribe();
    }
  }, [user]);

  const fetchSessions = useCallback(async () => {
    try {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
      const q = query(
        collection(db, 'conversations'), 
        where("userId", "==", user.uid),
        orderBy('lastUpdated', 'desc')
      );
  
      const querySnapshot = await getDocs(q);
      const fetchedSessions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name || t('untitledSession'),
        lastUpdated: doc.data().lastUpdated?.toDate() || new Date()
      }));
  
      const groupedSessions = {
        today: [],
        yesterday: [],
        previous7Days: [],
        previous30Days: [],
        older: []
      };
  
      fetchedSessions.forEach(session => {
        if (session.lastUpdated >= today) {
          groupedSessions.today.push(session);
        } else if (session.lastUpdated >= yesterday) {
          groupedSessions.yesterday.push(session);
        } else if (session.lastUpdated >= sevenDaysAgo) {
          groupedSessions.previous7Days.push(session);
        } else if (session.lastUpdated >= thirtyDaysAgo) {
          groupedSessions.previous30Days.push(session);
        } else {
          groupedSessions.older.push(session);
        }
      });
  
      setSessions(groupedSessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setError(t('fetchSessionsError'));
    }
  }, [user.uid, t]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  useEffect(() => {
    const fetchAgentStats = async () => {
      if (user) {
        try {
            // Fetch ALL VTC Data to aggregate chat counts
            const qVtc = query(collection(db, 'talentCards'), where('userId', '==', user.uid));
            const vtcSnap = await getDocs(qVtc);
            setTotalDigitalTwins(vtcSnap.size);
            let chats = 0;
            vtcSnap.forEach(doc => {
                chats += (doc.data().chatCount || 0);
            });
            setTotalConversations(chats);

            // Fetch Simulations for Screenings Passed
            const qSims = query(collection(db, 'simulations'), where('participant_uids', 'array-contains', user.uid));
            const simSnap = await getDocs(qSims);
            let passedCount = 0;
            simSnap.forEach(doc => {
                const simData = doc.data();
                if (simData.candidates) {
                    Object.values(simData.candidates).forEach(candidate => {
                        if (candidate.userId === user.uid && candidate.status === 'shortlisted') {
                            passedCount++;
                        }
                    });
                }
            });
            setScreeningsPassed(passedCount);

        } catch (e) {
            console.error("Error fetching agent stats", e);
        }
      }
    };
    fetchAgentStats();
  }, [user]);

  useEffect(() => {
    if (sessionId && user && user.uid) {
      const unsubscribe = onSnapshot(
        doc(db, 'conversations', sessionId),
        async (docSnapshot) => {
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            if (data.userId === user.uid) {
              const messages = data.messages || [];
              const visibleMessages = messages.filter(msg => !msg.isHidden);
              setDisplayedConversation(visibleMessages);
              setSessionName(data.name || t('untitledSession'));
              setError(null);
  
              if (data.verifiedTalentCardId) {
                const talentCardDoc = await getDoc(doc(db, 'talentCards', data.verifiedTalentCardId));
                if (talentCardDoc.exists()) {
                  const talentCardData = talentCardDoc.data();
                  setTalentCard(talentCardData);
                  setTalentCardUrl(talentCardData.url);
                } else {
                  setTalentCard(null);
                  setTalentCardUrl(null);
                }
              } else {
                setTalentCard(null);
                setTalentCardUrl(null);
              }
            } else {
              setError(t('permissionError'));
            }
          } else {
            setError(null);
            setSessionId(null);
            setSessionName('');
            setDisplayedConversation([]);
            setTalentCard(null);
            setTalentCardUrl(null);
            localStorage.removeItem('sessionId');
          }
        },
        (error) => {
          console.error("Error in Firestore snapshot listener:", error);
          setError(t('listenUpdatesError') + error.message);
        }
      );
      return () => unsubscribe();
    }
  }, [sessionId, user, t]);

  const streamAudio = useCallback((audioUrl) => {
    if (audioRef.current) {
      audioRef.current.src = audioUrl;
      audioRef.current.muted = isMuted;
      audioRef.current.play().catch(e => console.error("Error playing audio:", e));
    }
  }, [isMuted]);

  useEffect(() => {
    if (displayedConversation.length > 0) {
      const lastMessage = displayedConversation[displayedConversation.length - 1];
      if (lastMessage.role === 'model' && lastMessage.audioUrl) {
        streamAudio(lastMessage.audioUrl);
      }
    }
  }, [displayedConversation, streamAudio]);

  useEffect(() => {
    const initSession = async () => {
      if (isInitialized) return;

      const storedSessionId = initialSessionId || localStorage.getItem('sessionId');
      if (storedSessionId) {
        setSessionId(storedSessionId);
      } else if (initialSessionId) {
        setSessionId(initialSessionId);
        await callGeminiFunction('', topic, initialSessionId, true, isMuted, i18n.language);
      }
      
      await fetchSessions();
      setIsInitialized(true);
    };

    initSession();
  }, [fetchSessions, isInitialized, initialSessionId, isMuted, i18n.language]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [displayedConversation]);

  const callGeminiFunction = async (prompt, topic, sessionId, isInitializing, isMuted, language) => {
    setRetryCount(0);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const response = await fetch(`${backendUrl}/callGeminiMulti`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await user.getIdToken()}`
      },
      body: JSON.stringify({
        prompt, 
        topic,
        sessionId,
        isInitializing,
        isMuted,
        language
      })
    });

    if (response.status === 500) {
      setRetryCount(prev => prev + 1);
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (prompt.trim() === '' || !sessionId) return;

    const currentPrompt = prompt;
    setPrompt('');

    setIsLoading(true);
    setError(null);
    setRetryCount(0);

    try {
      const result = await callGeminiFunction(currentPrompt, topic, sessionId, false, isMuted, i18n.language);

      if (result.error) {
        throw new Error(result.error);
      }

      setAudioUrl(result.audioUrl);
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      let errorMessage = t('unexpectedError');
      if (error.message.includes('500 Internal Server Error')) {
        errorMessage = t('aiServiceError');
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSessionClick = useCallback(async (clickedSessionId) => {
    if (clickedSessionId === sessionId) {
       if (isMobile) setShowMobileSessionList(false);
       return;
    }
 
    setSessionId(clickedSessionId);
    localStorage.setItem('sessionId', clickedSessionId);
    if (isMobile) setShowMobileSessionList(false);
  }, [sessionId, isMobile]);

  const handleNewSession = async () => {
    const newSessionId = `session_${Date.now()}`;
    setIsLoading(true);
    setError(null);
    setShowAudioPrompt(true);
    setIsInitializingSession(true);
    
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const newSessionName = await runTransaction(db, async (transaction) => {
        const userDoc = await transaction.get(userDocRef);
        if (!userDoc.exists() || !userDoc.data().profile) {
          throw new Error("User profile not found!");
        }
        const newCounter = userDoc.data().profile.sessionCounter || 1;
        transaction.update(userDocRef, { "profile.sessionCounter": newCounter + 1 });
        return `${t('untitledSession')}-${newCounter.toString().padStart(2, '0')}`;
      });

      const docRef = doc(db, 'conversations', newSessionId);
      await setDoc(docRef, {
        topic: topic,
        messages: [],
        lastUpdated: serverTimestamp(),
        name: newSessionName,
        userId: user.uid
      });

      setSessionId(newSessionId);
 
      const result = await callGeminiFunction('', topic, newSessionId, true, isMuted, i18n.language);
  
      if (result.error) {
        throw new Error(result.error);
      }
  
      localStorage.setItem('sessionId', newSessionId);
      setSessionName(newSessionName);
      setAudioUrl(result.audioUrl);
      await fetchSessions();

    } catch (error) {
      console.error('Error in handleNewSession:', error);
      setError(t('initSessionError') + error.message);
    } finally {
      setIsLoading(false);
      setIsInitializingSession(false);
    }
  };

  useEffect(() => {
    if (displayedConversation.length > 0) {
      setShowAudioPrompt(false);
    }
  }, [displayedConversation]);

  const handleDeleteSession = useCallback(async (sessionIdToDelete) => {
    if (window.confirm(t('deleteSessionConfirm'))) {
      try {
        await deleteDoc(doc(db, 'conversations', sessionIdToDelete));
        if (sessionIdToDelete === sessionId) {
          setSessionId(null);
          setSessionName('');
          setDisplayedConversation([]);
          localStorage.removeItem('sessionId');
          setError(null);
        }
        await fetchSessions();
      } catch (error) {
        console.error('Error deleting session:', error);
        setError(t('deleteSessionError') + error.message);
      }
    }
  }, [sessionId, fetchSessions, t]);

  const handleRenameSession = useCallback(async (sessionId, newName) => {
    if (newName && newName.trim() !== '') {
      await updateDoc(doc(db, 'conversations', sessionId), {
        name: newName.trim(),
        lastUpdated: serverTimestamp()
      });
      if (sessionId === sessionId) {
        setSessionName(newName.trim());
      }
      fetchSessions();
      setEditingSessionId(null);
    }
  }, [sessionId, fetchSessions]);

  const toggleMute = useCallback(() => {
    setIsMuted(prevMuted => {
      const newMuted = !prevMuted;
      if (audioRef.current) {
        audioRef.current.muted = newMuted;
      }
      return newMuted;
    });
  }, []);

  const handleShareDigitalTwin = async (vtcId) => {
    try {
      if (ansBadgeRef.current) {
        const canvas = await html2canvas(ansBadgeRef.current, { useCORS: true, scale: 2 });
        await new Promise((resolve) => {
          canvas.toBlob(async (blob) => {
            if (blob) {
              try {
                await navigator.clipboard.write([
                  new ClipboardItem({ 'image/png': blob })
                ]);
                setNotification({ show: true, message: "Badge image copied! Paste it (Ctrl+V) into your LinkedIn post." });
                setTimeout(() => setNotification({ show: false, message: '' }), 5000);
              } catch (err) {
                console.error('Clipboard write failed', err);
                setNotification({ show: true, message: "Could not copy image. Please screenshot manually." });
                setTimeout(() => setNotification({ show: false, message: '' }), 5000);
              }
            }
            resolve();
          });
        });
      }
    } catch (e) {
      console.error("Screenshot failed", e);
    }

    const publicUrl = process.env.REACT_APP_PUBLIC_URL || window.location.origin;
    const url = `${publicUrl}/digital-twin/${vtcId}`;
    const text = t('myProfile.shareDigitalTwinText', "I just verified my AI AI Twin on POD+ ANS — and it’s live!\nTake a look:");
    const linkedinUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}%20${encodeURIComponent(url)}`;
    window.open(linkedinUrl, '_blank');
  };

  const [hoveredSessionId, setHoveredSessionId] = useState(null);

  // --- Rendering Helpers ---

  const filterSessions = (list) => {
    if (!searchQuery) return list;
    return list.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()));
  };

  const renderSessionGroup = (title, sessions) => {
    if (sessions.length === 0) return null;
    return (
      <div key={title}>
         {/* Date Group Header - Simplified to just text for now */}
        <div style={{ padding: '8px 16px', fontSize: '12px', fontWeight: '600', color: 'rgba(0,0,0,0.6)', textTransform: 'uppercase' }}>
            {title}
        </div>
        
        {sessions.map((session) => (
          <div 
            key={session.id} 
            className={`msg-conversation-card ${session.id === sessionId ? 'active' : ''}`}
            onClick={() => handleSessionClick(session.id)}
            onMouseEnter={() => setHoveredSessionId(session.id)}
            onMouseLeave={() => setHoveredSessionId(null)}
          >
            <div style={{ flexShrink: 0 }}>
               <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#e0f2f7', color: '#0a66c2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: 'bold' }}>
                  {session.name.charAt(0).toUpperCase()}
               </div>
            </div>

            <div className="msg-conversation-card__content--selectable">
                <div className="msg-conversation-card__rows">
                    <div className="msg-conversation-card__row msg-conversation-card__title-row" style={{ justifyContent: 'space-between', padding: 0, border: 'none' }}>
                        {editingSessionId === session.id ? (
                            <input
                                value={editingSessionName}
                                onChange={(e) => setEditingSessionName(e.target.value)}
                                onBlur={() => handleRenameSession(session.id, editingSessionName)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleRenameSession(session.id, editingSessionName); }}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                                style={{ width: '100%' }}
                            />
                        ) : (
                            <h3 className="msg-conversation-listitem__participant-names msg-conversation-card__participant-names truncate" style={{ flexGrow: 1, fontSize: '16px' }}>
                                {session.name}
                            </h3>
                        )}
                    </div>
                    <div className="msg-conversation-card__row msg-conversation-card__body-row">
                        <p className="msg-conversation-card__message-snippet">
                            {/* Message snippet or generic text */}
                        </p>
                    </div>
                </div>
            </div>

            {/* Separate Action Div on the Right */}
            <div style={{ marginLeft: '8px', display: 'flex', alignItems: 'flex-start', paddingTop: '2px' }}>
                {(hoveredSessionId === session.id || editingSessionId === session.id) ? (
                    <SessionOptionsMenu 
                        onRename={() => { setEditingSessionId(session.id); setEditingSessionName(session.name); }}
                        onDelete={() => handleDeleteSession(session.id)}
                        isVisible={true}
                    />
                ) : (
                    <time className="t-12 t-black--light" style={{ whiteSpace: 'nowrap' }}>
                        {session.lastUpdated ? session.lastUpdated.toLocaleDateString() : ''}
                    </time>
                )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderSuggestions = () => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, padding: '24px', textAlign: 'center', color: '#666' }}>
       <Logo size={32} style={{ marginBottom: '24px' }} />
       <h2 style={{ fontSize: '20px', marginBottom: '24px', fontWeight: '600' }}>{t('welcome.title', 'Welcome to your Career Agent')}</h2>
       
       <button onClick={handleNewSession} disabled={isInitializingSession} className="li-btn-primary" style={{ marginTop: '32px', padding: '12px 24px' }}>
         <PlusCircle size={18} style={{ marginRight: '8px' }} />
         {t('newChat')}
       </button>
    </div>
  );

  return (
    <div className="li-layout chat-mobile-fix">
      {notification.show && (
        <div className="li-success-msg-small" style={{ position: 'fixed', top: '20px', right: '20px', background: 'white', padding: '10px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', zIndex: 9999 }}>
          {notification.message}
        </div>
      )}
      
      {/* HEADER */}
      <div style={{ width: '100%', position: 'fixed', top: 0, left: 0, zIndex: 100 }}>
          <LinkedInHeader userProfile={localUserProfile} />
      </div>
      <MobileBottomNav 
        userProfile={localUserProfile} 
        showBusinessMenu={showBusinessMenu}
        setShowBusinessMenu={setShowBusinessMenu}
        showMeMenu={showMeMenu}
        setShowMeMenu={setShowMeMenu}
      />

      <div className="scaffold-layout__inner">
        <div className="scaffold-layout__row">
            
            {/* LEFT COLUMN: Session List & CENTER COLUMN: Chat Detail Wrapper */}
            <main className="scaffold-layout__list-detail">
                {/* Session List - Hidden on Mobile */}
                {!isMobile && (
                <div className="scaffold-layout__list-detail-container">
                    <div className="msg-cross-pillar-inbox-top-bar-wrapper__container">
                         <h1 className="t-16 t-black t-bold">{t('chat.messagingTitle')}</h1>
                         <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ position: 'relative' }}>
                                <Search size={16} style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#666' }} />
                                <input 
                                    type="text" 
                                    placeholder="Search sessions" 
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{ paddingLeft: '32px', height: '32px', borderRadius: '4px', border: '1px solid #e0e0e0', backgroundColor: '#eef3f8', width: '140px' }}
                                />
                            </div>
                            <button className="artdeco-button artdeco-button--circle artdeco-button--muted" onClick={handleNewSession}>
                                <MessageSquarePlus size={20} />
                            </button>
                         </div>
                    </div>

                    <div className="scaffold-layout__list-detail-inner">
                        <div className="scaffold-layout__list">
                            {renderSessionGroup(t('today'), filterSessions(sessions.today))}
                            {renderSessionGroup(t('yesterday'), filterSessions(sessions.yesterday))}
                            {renderSessionGroup(t('previous7Days'), filterSessions(sessions.previous7Days))}
                            {renderSessionGroup(t('previous30Days'), filterSessions(sessions.previous30Days))}
                            {renderSessionGroup(t('older'), filterSessions(sessions.older))}
                        </div>
                    </div>
                </div>
                )}

                {/* CENTER COLUMN: Chat Detail */}
                <div className="scaffold-layout__detail">
                    
                    {/* Chat Header */}
                    <div className="msg-title-bar">
                         <div className="msg-entity-lockup display-flex align-items-center">
                             {isMobile && (
                                 <button onClick={() => setShowMobileSessionList(true)} style={{ marginRight: '12px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                                     <Menu size={24} color="rgba(0,0,0,0.6)" />
                                 </button>
                             )}
                             <h2 className="t-16 t-black t-bold mr2">{sessionId ? sessionName : t('welcome.createTwinTitle')}</h2>
                         </div>
                         {sessionId && (
                            <span className="t-12 t-black--light" style={{ marginLeft: 'auto', marginRight: '8px' }}>{new Date().toLocaleDateString()}</span>
                         )}
                         <div className="msg-thread-actions">
                             <button onClick={toggleMute} className="artdeco-button artdeco-button--circle artdeco-button--muted">
                                {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                             </button>
                         </div>
                    </div>

                    {/* Chat Messages */}
                    <div className="msg-s-message-list-container">
                         {error && <div className="li-error-msg" style={{ padding: '16px' }}>{error}</div>}
                         
                         {!sessionId ? renderSuggestions() : (
                             <div className="chat-messages" ref={chatContainerRef} style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '16px' }}>
                                 {showAudioPrompt && (
                                    <div style={{ padding: '12px', backgroundColor: '#e8f0fe', borderRadius: '8px', color: '#0a66c2', fontSize: '13px', textAlign: 'center', marginBottom: '16px' }}>
                                        {t('audioPrompt')}
                                    </div>
                                 )}
                                 
                                 {displayedConversation.map((message, index) => {
                                    const isUser = message.role === 'user';
                                    return (
                                        <div key={index} style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start', marginBottom: '12px' }}>
                                            <div style={{ display: 'flex', gap: '8px', flexDirection: isUser ? 'row-reverse' : 'row', maxWidth: '80%' }}>
                                                <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: isUser ? '#0a66c2' : '#f3f2ef', overflow: 'hidden', flexShrink: 0 }}>
                                                    {isUser ? (
                                                        <img src={localUserProfile?.avatar_url || "https://via.placeholder.com/40"} alt="Me" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : (
                                                        <img src={process.env.PUBLIC_URL + "/Nia4.png"} alt="Nia" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: isUser ? '#e8f0fe' : '#f3f2ef', color: 'rgba(0,0,0,0.9)', fontSize: '14px', lineHeight: '1.5' }}>
                                                        {message.parts}
                                                    </div>
                                                    <span style={{ fontSize: '12px', color: 'rgba(0,0,0,0.6)', marginTop: '4px', alignSelf: isUser ? 'flex-end' : 'flex-start' }}>
                                                        {(() => {
                                                            const timestamp = parseInt(message.id);
                                                            return !isNaN(timestamp) ? new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                                                        })()}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                 })}
                             </div>
                         )}
                    </div>

                    {/* Input Area */}
                    {sessionId && (
                        <div className="msg-form">
                            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: '8px' }}>
                                <textarea
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    placeholder="Write a message..."
                                    className="t-14 t-black--light t-normal"
                                    style={{ 
                                        flex: 1, 
                                        border: '1px solid #e0e0e0', 
                                        borderRadius: '20px', 
                                        backgroundColor: '#f9f9f9',
                                        padding: '10px 12px',
                                        resize: 'none', 
                                        outline: 'none', 
                                        minHeight: '40px',
                                        maxHeight: '120px',
                                        fontFamily: 'inherit',
                                        boxSizing: 'border-box'
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSubmit(e);
                                        }
                                    }}
                                />
                                <button 
                                    type="submit" 
                                    disabled={isLoading || !prompt.trim()}
                                    className="li-btn-primary"
                                    style={{ borderRadius: '20px', padding: '0 16px', height: '40px', fontSize: '14px', flexShrink: 0 }}
                                >
                                    Send
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            </main>

            {/* RIGHT COLUMN: Aside - Hidden on Mobile */}
            {!isMobile && (
            <aside className="scaffold-layout__aside">
                 {talentCard ? (
                    <div className="li-card" style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h3 className="t-16 t-bold">{t('chat.matchedProfile')}</h3>
                            {talentCard?.ansRegistrationStatus === 'registered' && (
                                <button onClick={() => setShowAnsModal(true)} className="artdeco-button artdeco-button--circle" title="View AI Agent" style={{ color: '#057642', backgroundColor: '#e6f7ee' }}>
                                    <Bot size={18} />
                                </button>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                            <div style={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#b0b0b0', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: 'bold' }}>
                                {talentCard.name?.charAt(0)}
                            </div>
                            <div>
                                <div className="t-16 t-bold">{talentCard.name}</div>
                                <div className="t-12 t-black--light">{talentCard.title}</div>
                            </div>
                        </div>
                        <Link to={`/talent-card/${talentCardUrl?.split('/talent-card/')[1] || ''}`} className="li-btn-outline-primary">
                            View Full Profile
                        </Link>
                    </div>
                 ) : (
                    <div className="scaffold-layout__sticky scaffold-layout__sticky--is-active scaffold-layout__sticky--lg">
                        <div className="scaffold-layout__sticky-content">
                            <section className="ad-banner-container artdeco-card overflow-hidden" style={{ padding: 0, marginBottom: '16px', position: 'relative' }}>
                                <a href="https://gclouds.co.uk/ans" target="_blank" rel="noreferrer" style={{ display: 'block', width: '100%' }}>
                                     {i18n.language.startsWith('tr') ? (
                                         <img src={process.env.PUBLIC_URL + "/Flat_illutrations/interview.png"} alt="POD+ İş bulma ve işe yerleştirme platformu" style={{ width: '100%', height: 'auto', display: 'block' }} />
                                     ) : (
                                         <img src={process.env.PUBLIC_URL + "/pod-banner-en.jpg"} alt="POD+ All-in-One Talent Acquisition" style={{ width: '100%', height: 'auto', display: 'block' }} />
                                     )}
                                </a>
                            </section>

                            {/* Agent Performance Promo Card */}
                            <div className="li-card" style={{ padding: 0, marginBottom: '16px' }}>
                                <div style={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0' }}>
                                     <h2 style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>{t('chat.agentActivity')}</h2>
                                     <span className="t-12 t-black--light">{t('chat.networkStats')}</span>
                                </div>
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                    <li style={{ padding: '8px 16px', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div className="t-14 t-black">{t('chat.totalTwins')}</div>
                                        <div className="t-14 t-bold">{totalDigitalTwins}</div>
                                    </li>
                                    <li style={{ padding: '8px 16px', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div className="t-14 t-black">{t('chat.totalConversations')}</div>
                                        <div className="t-14 t-bold">{totalConversations}</div>
                                    </li>
                                    <li style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div className="t-14 t-black">{t('chat.screeningsPassed')}</div>
                                        <div className="t-14 t-bold">{screeningsPassed}</div>
                                    </li>
                                </ul>
                                <div style={{ padding: '12px', borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
                                     <Link to="/agent-performance" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', color: '#666', fontWeight: '600', fontSize: '14px', textDecoration: 'none' }}>
                                        <BarChart2 size={16} />
                                        Show full analytics
                                     </Link>
                                </div>
                            </div>
                            
                            <div className="li-footer-links">
                                <Link to="/about">{t('footer.about')}</Link>
                                <Link to="/accessibility">{t('footer.accessibility')}</Link>
                                <Link to="/help-center">{t('footer.helpCenter')}</Link>
                                <Link to="/privacy-terms">{t('footer.privacyTerms')}</Link>
                                <span>{t('auth.footer.rights')}</span>
                            </div>
                        </div>
                    </div>
                 )}
            </aside>
            )}

        </div>
      </div>

      <audio ref={audioRef} style={{ display: 'none' }} />

      {/* Mobile Session List Overlay */}
      {isMobile && showMobileSessionList && (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'white',
            zIndex: 2000,
            display: 'flex',
            flexDirection: 'column'
        }}>
            <div className="msg-cross-pillar-inbox-top-bar-wrapper__container" style={{ padding: '12px 16px', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '52px', boxSizing: 'border-box' }}>
                 <h1 className="t-16 t-black t-bold">{t('chat.messagingTitle')}</h1>
                 <div style={{display: 'flex', gap: '16px', alignItems: 'center'}}>
                     <button onClick={() => { handleNewSession(); setShowMobileSessionList(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                        <MessageSquarePlus size={24} color="rgba(0,0,0,0.6)" />
                     </button>
                     <button onClick={() => setShowMobileSessionList(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                        <X size={24} color="rgba(0,0,0,0.9)" />
                     </button>
                 </div>
            </div>
            <div style={{ padding: '12px 16px' }}>
                 <div style={{ position: 'relative' }}>
                    <Search size={16} style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#666' }} />
                    <input 
                        type="text" 
                        placeholder="Search sessions" 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{ paddingLeft: '32px', height: '36px', borderRadius: '4px', border: '1px solid #e0e0e0', backgroundColor: '#eef3f8', width: '100%', boxSizing: 'border-box' }}
                    />
                </div>
            </div>
            <div className="scaffold-layout__list-detail-inner" style={{ flex: 1, overflowY: 'auto' }}>
                <div className="scaffold-layout__list">
                    {renderSessionGroup(t('today'), filterSessions(sessions.today))}
                    {renderSessionGroup(t('yesterday'), filterSessions(sessions.yesterday))}
                    {renderSessionGroup(t('previous7Days'), filterSessions(sessions.previous7Days))}
                    {renderSessionGroup(t('previous30Days'), filterSessions(sessions.previous30Days))}
                    {renderSessionGroup(t('older'), filterSessions(sessions.older))}
                </div>
            </div>
        </div>
      )}
      
      {/* ANS Modal */}
      {showAnsModal && talentCard && (
        <div className="ans-modal-overlay" onClick={() => setShowAnsModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="ans-badge-container" ref={ansBadgeRef} onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', maxWidth: '400px', width: '90%', textAlign: 'center' }}>
            <div className="ans-badge-header" style={{ marginBottom: '16px' }}>
              <Logo size={24} className="logo" />
            </div>
            <div className="ans-badge-body">
              <div className="avatar-placeholder" style={{ width: '80px', height: '80px', margin: '0 auto 16px', borderRadius: '50%', overflow: 'hidden', backgroundColor: '#f3f2ef' }}>
                {avatarUrl ? (
                  <img src={avatarUrl} alt="User avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Bot size={40} style={{ margin: '20px' }} />
                )}
              </div>
              <div className="ans-agent-info" style={{ marginBottom: '24px' }}>
                <h2 className="ans-agent-name" style={{ fontSize: '18px', fontWeight: '600' }}>{talentCard.name}</h2>
                <p className="ans-agent-title" style={{ color: '#666' }}>{talentCard.title}</p>
              </div>
            </div>
            <div className="ans-badge-footer">
              <div className="ans-info-item" style={{ marginBottom: '20px', backgroundColor: '#f3f2ef', padding: '8px', borderRadius: '4px', fontSize: '12px' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '4px' }}>{t('chat.agentId')}</label>
                <code style={{ fontFamily: 'monospace', color: '#0a66c2' }}>{`pod.${talentCard.userId}.${talentCardUrl?.split('/talent-card/')[1]}.ans`}</code>
              </div>
              <div className="ans-badge-verification" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#057642', fontWeight: '600' }}>
                <CheckCircle size={16} />
                <span>{t('verifiedOnAns')}</span>
              </div>
              <div style={{ marginTop: '16px' }}>
                <button 
                  onClick={() => handleShareDigitalTwin(talentCardUrl?.split('/talent-card/')[1])}
                  className="li-btn-primary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', width: '100%', justifyContent: 'center' }}
                >
                  <svg viewBox="0 0 32 32" fill="currentColor" width="20px" height="20px">
                    <path d="M26,2H6C3.8,2,2,3.8,2,6v20c0,2.2,1.8,4,4,4h20c2.2,0,4-1.8,4-4V6C30,3.8,28.2,2,26,2z M12,26H8V12h4V26z M10,10 c-1.1,0-2-0.9-2-2s0.9-2,2-2s2,0.9,2,2S11.1,10,10,10z M26,26h-4v-8c0-1.1-0.4-2-1.5-2S19,17.1,19,18v8h-4V12h4v2 c0.6-1,1.9-1.5,2.5-1.5c1.5,0,3.5,1,3.5,4V26z"></path>
                  </svg>
                  {t('shareOnLinkedIn', 'Share on LinkedIn')}
                </button>
              </div>
            </div>
            <div className="footer-note" style={{ marginTop: '16px', fontSize: '12px', color: '#666' }}>
              <p className="pod-description"><strong>POD+</strong> {t('podDescription')}</p>
              <p className="powered-by-ans" style={{ marginTop: '4px' }}>
                <Trans i18nKey="poweredByAns">
                  Powered by <strong>ANS (Agent Network System)</strong>.
                </Trans>
                {' '}
                <a href="https://www.gclouds.co.uk/ans" target="_blank" rel="noopener noreferrer" style={{ color: '#0a66c2' }}>{t('learnMore')}</a>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Chat;